import { useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { format, subMonths } from 'date-fns';
import { ExportToCsv } from 'export-to-csv';
import { Chart } from 'react-chartjs-2';
import { useQuery } from 'react-query';
import Subtitle from 'src/components/Subtitle';
import { getFiagroAssetData } from 'src/services/operation/operationFiagro';
import { FiagroAssetRaw } from 'src/types/operations/fiagro';
import { convertValue, toCurrency } from 'src/utils/number';

import csvSvg from '../../../../../assets/imgs/csv.svg';
import { formatDate, formatDateShort } from '../utils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
);

type PeriodFilter = 12 | 60 | 0;
type PortfolioSeries = 'totalInvestido' | 'vencidos' | 'normalizedDelay';

const PORTFOLIO_CHIPS: { key: PortfolioSeries; label: string }[] = [
  { key: 'totalInvestido', label: 'Carteira de Ativos' },
  { key: 'vencidos', label: 'Atrasos' },
  { key: 'normalizedDelay', label: 'Atraso Normalizado' },
];

const PERIOD_OPTIONS: { key: PeriodFilter; label: string }[] = [
  { key: 12, label: '12 meses' },
  { key: 60, label: '60 meses' },
  { key: 0, label: 'Ótimo' },
];

const ACTIVE_BORDER = '2px solid #FF8211';

export default function AssetPortfolio({ hash }: { hash: string }) {
  const [isTableView, setIsTableView] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<PortfolioSeries[]>([
    'totalInvestido',
  ]);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>(0);
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);

  const startDate =
    periodFilter !== 0
      ? format(subMonths(new Date(), periodFilter), 'yyyy-MM-dd')
      : undefined;

  const { data: rawData = [], isLoading } = useQuery({
    queryKey: ['operationFIAGROAssetPortfolio', hash, periodFilter],
    queryFn: () => getFiagroAssetData(hash, 1000, startDate),
  });

  const sortedDesc = [...rawData].sort((a, b) =>
    b.dataReferencia.localeCompare(a.dataReferencia),
  );

  const sortedAsc = [...rawData].sort((a, b) =>
    a.dataReferencia.localeCompare(b.dataReferencia),
  );

  const handleChipToggle = (key: PortfolioSeries) => {
    setSelectedSeries((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleExport = () => {
    const exporter = new ExportToCsv({
      fieldSeparator: ';',
      filename: 'fiagro_portfolio',
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: false,
      headers: ['Data', 'Carteira de Ativos', 'Atrasos', 'Atraso Normalizado'],
    });
    exporter.generateCsv(
      sortedDesc.map((d) => ({
        data: formatDate(d.dataReferencia),
        carteiraAtivos: toCurrency(d.totalInvestido),
        atrasos: toCurrency(d.vencidos),
        atrassoNormalizado:
          d.nivelAtrasosCarteira != null
            ? `${(d.nivelAtrasosCarteira * 100).toFixed(2)}%`
            : 'N/D',
      })),
    );
  };

  const showNormalized = selectedSeries.includes('normalizedDelay');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const portfolioDatasets: any[] = [
    selectedSeries.includes('totalInvestido') && {
      type: 'bar',
      label: 'Carteira de Ativos',
      data: sortedAsc.map((d) => d.totalInvestido),
      backgroundColor: '#1D1D1D',
    },
    selectedSeries.includes('vencidos') && {
      type: 'bar',
      label: 'Atrasos',
      data: sortedAsc.map((d) => d.vencidos),
      backgroundColor: '#FF8211',
    },
    showNormalized && {
      type: 'line',
      label: 'Atraso Normalizado',
      data: sortedAsc.map((d) =>
        d.nivelAtrasosCarteira != null
          ? Number((d.nivelAtrasosCarteira * 100).toFixed(4))
          : 0,
      ),
      borderColor: '#FF8211',
      backgroundColor: 'rgba(255, 130, 17, 0.2)',
      yAxisID: 'yLine',
      order: -1,
    },
  ].filter(Boolean);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const portfolioOptions: any = {
    plugins: {
      legend: { display: true, onClick: () => {} },
      tooltip: {
        callbacks: {
          label: (tooltipItem: {
            dataset: { yAxisID?: string };
            label: string;
            raw: unknown;
          }) => {
            const val = tooltipItem.raw as number;
            if (tooltipItem.dataset.yAxisID === 'yLine') {
              return `${tooltipItem.label}: ${val.toFixed(2)}%`;
            }
            return `${tooltipItem.label}: ${convertValue(val)}`;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: { callback: (value: number) => convertValue(value) },
      },
      yLine: {
        display: showNormalized,
        position: 'right',
        ticks: { callback: (value: number) => `${value.toFixed(0)}%` },
      },
    },
    maintainAspectRatio: false,
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton
          variant="text"
          width={150}
          height={20}
          sx={{ mb: 1, mt: 3 }}
        />
        <Box sx={{ height: 2, backgroundColor: 'black', mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={520} />
      </Box>
    );
  }

  return (
    <>
      <Subtitle
        title="Carteira"
        hasSelectType
        isSelectedType={isTableView}
        handleChangeType={() => setIsTableView((prev) => !prev)}
        titleType1="Gráfico"
        titleType2="Tabela"
      />
      <Paper square sx={{ padding: '24px 18px', mb: 4 }}>
        {!isTableView && (
          <>
            <Grid container spacing={1} justifyContent="center" sx={{ mb: 2 }}>
              {PORTFOLIO_CHIPS.map(({ key, label }) => (
                <Grid item key={key}>
                  <Chip
                    label={label}
                    color={selectedSeries.includes(key) ? 'primary' : 'default'}
                    variant={
                      selectedSeries.includes(key) ? 'filled' : 'outlined'
                    }
                    onClick={() => handleChipToggle(key)}
                    sx={{ cursor: 'pointer' }}
                  />
                </Grid>
              ))}
            </Grid>

            <Box display="flex" gap={1} justifyContent="flex-end" mb={1}>
              {PERIOD_OPTIONS.map(({ key, label }) => (
                <Typography
                  key={key}
                  variant="button"
                  onClick={() => setPeriodFilter(key)}
                  sx={{
                    cursor: 'pointer',
                    borderBottom:
                      periodFilter === key
                        ? ACTIVE_BORDER
                        : '2px solid transparent',
                    textTransform: 'none',
                  }}>
                  {label}
                </Typography>
              ))}
            </Box>

            <Box sx={{ height: 520 }}>
              <Chart
                type="bar"
                data={{
                  labels: sortedAsc.map((d) =>
                    formatDateShort(d.dataReferencia),
                  ),
                  datasets: portfolioDatasets,
                }}
                options={portfolioOptions}
              />
            </Box>
          </>
        )}

        {isTableView && (
          <>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                color="primary"
                sx={{ fontSize: '16px' }}
                startIcon={
                  <Box
                    component="img"
                    src={csvSvg}
                    alt="export"
                    sx={{ width: '20px', height: '20px' }}
                  />
                }
                onClick={handleExport}>
                Exportar
              </Button>
            </Box>
            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>
                      Data
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, fontSize: '12px' }}>
                      Carteira de Ativos
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, fontSize: '12px' }}>
                      Atrasos
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, fontSize: '12px' }}>
                      Atraso Normalizado
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDesc
                    .slice(
                      tablePage * tableRowsPerPage,
                      (tablePage + 1) * tableRowsPerPage,
                    )
                    .map((row: FiagroAssetRaw) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ fontSize: '0.8em' }}>
                          {formatDate(row.dataReferencia)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8em' }}>
                          {toCurrency(row.totalInvestido)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8em' }}>
                          {toCurrency(row.vencidos)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8em' }}>
                          {row.nivelAtrasosCarteira != null
                            ? `${(row.nivelAtrasosCarteira * 100).toFixed(2)}%`
                            : 'N/D'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[10, 30, 50]}
                      count={sortedDesc.length}
                      page={tablePage}
                      onPageChange={(_, newPage) => setTablePage(newPage)}
                      rowsPerPage={tableRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setTableRowsPerPage(parseInt(e.target.value, 10));
                        setTablePage(0);
                      }}
                      labelRowsPerPage="Registros por página"
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} de ${count}`
                      }
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </>
  );
}
